// src/pages/gt3x/ResultsSpecialX.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const SHEET_ID = "1bdWoKQm-B96F6fGmeB7Lo0BAiKDglXsQboXaVdtkIZI";
const SCHEDULE_RANGE = "Schedule!A1:D";
const RESULTS_META_RANGE = "ResultsMeta!A1:Z50";

const SHOW_RACE_TEAM_COLUMNS = false;
const SHOW_RACE_PEN_COLUMN = false;

// ---------- Helpers ----------
function colIndexFromLetters(letters) {
    let n = 0;
    for (let i = 0; i < letters.length; i++) n = n * 26 + (letters.charCodeAt(i) - 64);
    return n;
}
function letterToIndex(letter) {
    const num = colIndexFromLetters(letter);
    return num - 1;
}
function colLettersFromIndex(n1) {
    let n = n1, out = "";
    while (n > 0) {
        const rem = (n - 1) % 26;
        out = String.fromCharCode(65 + rem) + out;
        n = Math.floor((n - 1) / 26);
    }
    return out;
}
function lettersBetween(start, end) {
    const s = colIndexFromLetters(start);
    const e = colIndexFromLetters(end);
    const list = [];
    for (let i = s; i <= e; i++) list.push(colLettersFromIndex(i));
    return list;
}
function safeVal(row, idx) {
    const v = row?.[idx];
    return v === null || v === undefined ? "" : String(v).trim();
}
function parseSignedIntLike(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    const m = s.match(/[+-]?\d+/);
    if (!m) return null;
    const n = parseInt(m[0], 10);
    return Number.isNaN(n) ? null : n;
}
function parseLapTimeToSeconds(v) {
    const s = String(v ?? "").trim();
    if (!s) return null;

    const up = s.toUpperCase();
    if (up === "DNS" || up === "DNF" || up === "BAN") return null;

    const m = s.match(/^(\d+):(\d{2})(?:\.(\d{1,3}))?$/);
    if (m) {
        const min = Number(m[1]);
        const sec = Number(m[2]);
        const ms = m[3] ? Number(m[3].padEnd(3, "0")) : 0;
        return min * 60 + sec + ms / 1000;
    }

    const f = Number(s.replace(",", "."));
    return Number.isFinite(f) ? f : null;
}
function isNumeric(v) {
    return v !== null && v !== "" && !isNaN(Number(v));
}
function isSheetMissingResponse(res) {
    if (!res || typeof res !== "object") return true;
    const hasHeader = Array.isArray(res.header) && res.header.length > 0;
    const hasRows = Array.isArray(res.rows) && res.rows.length > 0;
    return !hasHeader && !hasRows;
}
function normalizeMetaRows(res) {
    if (Array.isArray(res?.header) && Array.isArray(res?.rows)) {
        return res.rows.map((r) => res.header.map((h) => r[h] ?? ""));
    }
    return [];
}
async function sheetExists(range) {
    try {
        const res = await fetchSheetViaProxy({ range, sheetId: SHEET_ID });
        return !isSheetMissingResponse(res);
    } catch {
        return false;
    }
}
function sanitizeToken(value) {
    return String(value ?? "")
        .trim()
        .toUpperCase()
        .replace(/[\/\\]+/g, "_")
        .replace(/\s+/g, "_")
        .replace(/[^A-Z0-9_-]/g, "")
        .replace(/^_+|_+$/g, "");
}

const imgFlag = (src) => <img src={src} alt="Flag" className="flag" loading="lazy" />;
const imgCar = (src) => <img src={src} alt="Car" className="carlogo" loading="lazy" />;

// ---------- View-Builder ----------
function buildRaceView(header, rows) {
    const hIndex = (L) => letterToIndex(L);

    let visCols = lettersBetween("A", "N");

    if (!SHOW_RACE_TEAM_COLUMNS) {
        visCols = visCols.filter((L) => L !== "H" && L !== "I");
    }

    if (!SHOW_RACE_PEN_COLUMN) {
        visCols = visCols.filter((L) => L !== "L");
    }

    const baseIdx = visCols.map((L) => hIndex(L));

    const idxFlagURL = hIndex("S");
    const idxCarURL = hIndex("T");

    const idxCarNo = hIndex("E");
    const idxDriver = hIndex("G");

    const posOf = (letter) => visCols.indexOf(letter);

    const toD = posOf("D");
    const toE = posOf("E");
    const toF = posOf("F");
    const toG = posOf("G");

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    if (toE >= 0) viewHeader[toE] = "#";

    const startIdx = posOf("B");
    if (startIdx >= 0 && (viewHeader[startIdx] || "").toLowerCase() === "start pos") {
        viewHeader[startIdx] = "Start";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc && toD >= 0) row[toD] = imgFlag(flagSrc);

        if (toE >= 0) row[toE] = r[idxCarNo];

        const carSrc = safeVal(r, idxCarURL);
        if (carSrc && toF >= 0) row[toF] = imgCar(carSrc);

        if (toG >= 0) row[toG] = r[idxDriver];

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

function buildQualyPracticeView(header, rows) {
    const hIndex = (L) => letterToIndex(L);
    const visCols = lettersBetween("A", "G");
    const baseIdx = visCols.map((L) => hIndex(L));

    const idxFlagURL = hIndex("J");
    const idxAutoURL = hIndex("K");

    const targetB = hIndex("B");
    const targetD = hIndex("D");

    const viewHeader = visCols.map((L) => header[hIndex(L)] ?? L);

    const visibleCpos = 2;
    if ((viewHeader[visibleCpos] || "").toLowerCase().includes("fahrzeugnummer")) {
        viewHeader[visibleCpos] = "#";
    }

    const viewRows = rows.map((r) => {
        const row = baseIdx.map((ci) => r[ci]);

        const flagSrc = safeVal(r, idxFlagURL);
        if (flagSrc) row[targetB - hIndex("A")] = imgFlag(flagSrc);

        const carSrc = safeVal(r, idxAutoURL);
        if (carSrc) row[targetD - hIndex("A")] = imgCar(carSrc);

        return row;
    });

    return { header: viewHeader, rows: viewRows };
}

function sessionDisplayLabel(value) {
    const v = String(value || "").toUpperCase();
    if (v === "RACE") return "RACE";
    if (v === "HEAT") return "HEAT";
    if (v === "FEATURE") return "FEATURE";
    if (v === "QUALY") return "QUALY";
    if (v === "PRACTICE") return "PRACTICE";
    return v;
}

export default function ResultsSpecialX() {
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);
    const [trackLabel, setTrackLabel] = useState("");

    const [session, setSession] = useState("RACE");
    const [klass, setKlass] = useState("GESAMT");

    const [sessionOptions, setSessionOptions] = useState([
        { value: "RACE", label: "RACE" },
        { value: "QUALY", label: "QUALY" },
        { value: "PRACTICE", label: "PRACTICE" },
    ]);
    const [classOptions, setClassOptions] = useState([{ value: "GESAMT", label: "Gesamt" }]);

    const [viewHeader, setViewHeader] = useState([]);
    const [viewRows, setViewRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    fetchSheetViaProxy,
                    SCHEDULE_RANGE,
                    "results",
                    SHEET_ID
                );
                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto-race failed (results)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    // Optionen für aktuelle Runde erkennen
    useEffect(() => {
        (async () => {
            if (!race) return;

            const nextSessionOptions = [];
            const nextClassOptions = [{ value: "GESAMT", label: "Gesamt" }];

            // Practice / Qualy prüfen
            if (await sheetExists(`R${race}_PRACTICE!A1:K3`)) {
                nextSessionOptions.push({ value: "PRACTICE", label: "PRACTICE" });
            }
            if (await sheetExists(`R${race}_QUALY!A1:K3`)) {
                nextSessionOptions.push({ value: "QUALY", label: "QUALY" });
            }

            // Race / Heat / Feature prüfen
            const hasHeat = await sheetExists(`R${race}_HEAT!A1:U3`);
            const hasFeature = await sheetExists(`R${race}_FEATURE!A1:U3`);
            const hasRace = await sheetExists(`R${race}_RACE!A1:U3`);

            if (hasHeat) nextSessionOptions.push({ value: "HEAT", label: "HEAT" });
            if (hasFeature) nextSessionOptions.push({ value: "FEATURE", label: "FEATURE" });
            if (!hasHeat && !hasFeature && hasRace) {
                nextSessionOptions.push({ value: "RACE", label: "RACE" });
            }

            // Klassen aus optionalem Meta-Tab lesen
            try {
                const metaRes = await fetchSheetViaProxy({
                    range: RESULTS_META_RANGE,
                    sheetId: SHEET_ID,
                });

                const metaRows = normalizeMetaRows(metaRes);
                for (const row of metaRows) {
                    const rowRace = String(row?.[0] ?? "").trim();
                    const rowClassLabel = String(row?.[1] ?? "").trim();
                    const rowClassToken = sanitizeToken(row?.[2] ?? "");
                    if (String(rowRace) !== String(race)) continue;
                    if (!rowClassLabel || !rowClassToken) continue;

                    nextClassOptions.push({
                        value: rowClassToken,
                        label: rowClassLabel,
                    });
                }
            } catch {
                // bewusst still: ohne Meta einfach keine Klassenauswahl
            }

            // Fallback Standard
            if (!nextSessionOptions.length) {
                nextSessionOptions.push(
                    { value: "RACE", label: "RACE" },
                    { value: "QUALY", label: "QUALY" },
                    { value: "PRACTICE", label: "PRACTICE" }
                );
            }

            setSessionOptions(nextSessionOptions);
            setClassOptions(nextClassOptions);

            const sessionStillValid = nextSessionOptions.some((o) => o.value === session);
            if (!sessionStillValid) {
                setSession(nextSessionOptions[0]?.value ?? "RACE");
            }

            const classStillValid = nextClassOptions.some((o) => o.value === klass);
            if (!classStillValid) {
                setKlass("GESAMT");
            }
        })();
    }, [race]); // bewusst nur auf Runde

    useEffect(() => {
        (async () => {
            if (!race || !session) return;

            setLoading(true);
            setMsg("");
            setViewHeader([]);
            setViewRows([]);

            try {
                let range = "";
                let buildView = buildRaceView;

                if (session === "PRACTICE") {
                    buildView = buildQualyPracticeView;
                    range =
                        klass !== "GESAMT"
                            ? `R${race}_PRACTICE_${klass}!A1:K100`
                            : `R${race}_PRACTICE!A1:K100`;
                } else if (session === "QUALY") {
                    buildView = buildQualyPracticeView;
                    range =
                        klass !== "GESAMT"
                            ? `R${race}_QUALY_${klass}!A1:K100`
                            : `R${race}_QUALY!A1:K100`;
                } else if (session === "HEAT") {
                    buildView = buildRaceView;
                    range =
                        klass !== "GESAMT"
                            ? `R${race}H_${klass}!A1:U100`
                            : `R${race}_HEAT!A1:U100`;
                } else if (session === "FEATURE") {
                    buildView = buildRaceView;
                    range =
                        klass !== "GESAMT"
                            ? `R${race}F_${klass}!A1:U100`
                            : `R${race}_FEATURE!A1:U100`;
                } else {
                    buildView = buildRaceView;
                    range =
                        klass !== "GESAMT"
                            ? `R${race}_${klass}!A1:U100`
                            : `R${race}_RACE!A1:U100`;
                }

                const res = await fetchSheetViaProxy({
                    range,
                    sheetId: SHEET_ID,
                });

                if (isSheetMissingResponse(res)) {
                    setMsg("Für diese Auswahl gibt es noch keine Ergebnisse.");
                    return;
                }

                const header = Array.isArray(res?.header) ? res.header : [];
                const rows = Array.isArray(res?.rows)
                    ? res.rows.map((r) => header.map((h) => r[h]))
                    : [];

                const { header: vHeader, rows: vRows } = buildView(header, rows);
                setViewHeader(vHeader);
                setViewRows(vRows);
            } catch (e) {
                console.warn("Results load failed", e);
                setMsg("Für diese Auswahl gibt es noch keine Ergebnisse.");
            } finally {
                setLoading(false);
            }
        })();
    }, [race, session, klass]);

    const colIdxMap = useMemo(() => {
        const map = new Map();
        viewHeader.forEach((h, i) => map.set((h || "").toLowerCase(), i));
        return map;
    }, [viewHeader]);

    const deltaCol = useMemo(() => {
        if (session === "RACE" || session === "HEAT" || session === "FEATURE") return 2;
        return colIdxMap.get("delta pos");
    }, [session, colIdxMap]);

    const numericCols = useMemo(() => {
        const set = new Set();
        viewRows.forEach((row) =>
            row.forEach((cell, i) => {
                if (isNumeric(cell)) set.add(i);
            })
        );
        return set;
    }, [viewRows]);

    const fastestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) =>
            String(h ?? "").toLowerCase().includes("fastest")
        );
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const fastestLapBest = useMemo(() => {
        if (fastestLapCol === null) return null;

        let best = null;
        for (const row of viewRows) {
            const t = parseLapTimeToSeconds(row?.[fastestLapCol]);
            if (t === null) continue;
            if (best === null || t < best) best = t;
        }
        return best;
    }, [viewRows, fastestLapCol]);

    const avgLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("avg lap") || s.includes("average lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    const bestLapCol = useMemo(() => {
        const idx = viewHeader.findIndex((h) => {
            const s = String(h ?? "").toLowerCase();
            return s.includes("best lap");
        });
        return idx >= 0 ? idx : null;
    }, [viewHeader]);

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                autoSelectFirst={false}
                valueSession={session}
                onSessionChange={setSession}
                showSession={true}
                valueClass={klass}
                onClassChange={setKlass}
                showClass={classOptions.length > 1}
                sessionOptions={sessionOptions}
                classOptions={classOptions}
                onTrackLabelChange={setTrackLabel}
                sheetId={SHEET_ID}
                scheduleRange={SCHEDULE_RANGE}
                raceLabelPrefix="R"
            />

            <div className="card overview-card-top">
                <h1>Ergebnisse</h1>
                <p className="muted results-subtitle">
                    {sessionDisplayLabel(session)}{" "}
                    <span className="pill pill--gesamt">
                        {klass === "GESAMT" ? "Gesamt" : classOptions.find((x) => x.value === klass)?.label ?? klass}
                    </span>
                    {trackLabel ? <> · {trackLabel}</> : null}
                </p>
            </div>

            {msg && (
                <div className="card">
                    <p className="text-dim">{msg}</p>
                </div>
            )}

            {loading && (
                <div className="card">
                    <p>Lade…</p>
                </div>
            )}

            {!loading && !msg && (
                <div className="fancyTable">
                    <table className="tbl">
                        <thead>
                        <tr>
                            {viewHeader.map((h, i) => {
                                const isAvg = avgLapCol !== null && i === avgLapCol;
                                const isBest = bestLapCol !== null && i === bestLapCol;

                                return (
                                    <th key={i} className={(isAvg || isBest) ? "lap-center" : ""}>
                                        {h}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>

                        <tbody>
                        {viewRows.map((r, ri) => (
                            <tr key={ri}>
                                {r.map((cell, ci) => {
                                    let cls = "";

                                    if (numericCols.has(ci)) cls = "num";

                                    if (typeof deltaCol === "number" && ci === deltaCol) {
                                        const n = parseSignedIntLike(cell);
                                        if (n !== null) {
                                            if (n > 0) cls = `${cls} delta-up`.trim();
                                            else if (n < 0) cls = `${cls} delta-down`.trim();
                                        }
                                    }

                                    const headerName = (viewHeader?.[ci] || "").toLowerCase();
                                    if (headerName.includes("pen") && String(cell).trim() !== "") {
                                        cls = `${cls} penalty-red`.trim();
                                    }

                                    if (fastestLapCol !== null && fastestLapBest !== null && ci === fastestLapCol) {
                                        const t = parseLapTimeToSeconds(cell);
                                        if (t !== null && Math.abs(t - fastestLapBest) < 0.0005) {
                                            cls = `${cls} fastest-best`.trim();
                                        }
                                    }

                                    if (avgLapCol !== null && ci === avgLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }
                                    if (bestLapCol !== null && ci === bestLapCol) {
                                        cls = `${cls} lap-center`.trim();
                                    }

                                    return (
                                        <td key={ci} className={cls}>
                                            {cell}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}